import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { DownloadIcon, Calender, Employee, Eye, SearchBig, Active, Office, CircularGrid } from "../AllSvg";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { CustomImageModal, getOrdinal } from "../CustomFunctions";
import UserDefaultLogo from "../UserDetails/UserDefaultLogo";
import { downloadAsExcel } from "../CustomFunctions";
import { BASE_URL, IMAGE_URL } from "../../config/axios";
import usePermission from "../../config/permissions";

const getCurrentMonthYear = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
};

const getNextMonthYear = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");

    return `${year}-${month}`;
};

const StarIcon = ({ fillPercentage, id }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset={`${fillPercentage}%`} stopColor="#FFC107" />
                <stop offset={`${fillPercentage}%`} stopColor="#f0eded" />
            </linearGradient>
        </defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={`url(#grad-${id})`} stroke="" strokeWidth="1" />
    </svg>
);

const EmployeeStarRanking = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [letterConfig, setLetterConfig] = useState(null);
    const [overallTotals, setOverallTotals] = useState({
        attendance_total_marks: "-",
        letter_total_marks: "-",
        overall_total_marks: "-",
    });

    // ================= TAB STATE =================
    const [activeTab, setActiveTab] = useState("attendance"); // "attendance" | "letter"

    // ================= API-TRIGGERING FILTERS =================
    const [toggleCompany, setToggleCompany] = useState(`${sessionStorage.getItem("company_id")}`);
    const [startMonthYear, setStartMonthYear] = useState(getCurrentMonthYear());
    const [endMonthYear, setEndMonthYear] = useState(getCurrentMonthYear());
    const [deptFilter, setDeptFilter] = useState("null");
    const [empStatus, setEmpStatus] = useState("active");
    const [rankFrom, setRankFrom] = useState("");
    const [rankTo, setRankTo] = useState("");
    const [sortOrder, setSortOrder] = useState("high_to_low");
    const { SUB_COMPANIES, SUPER_ADMIN } = usePermission();

    // ================= LOCAL SEARCH FILTERS (No API calls) =================
    const [empSearch, setEmpSearch] = useState("");
    const [rhSearch, setRhSearch] = useState("");

    // ================= MODAL & EXPORT STATE =================
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const tableRef = useRef(null);
    const hiddenDownloadBtnRef = useRef(null);

    // State to hold fetched departments
    const [departmentLists, setDepartmentLists] = useState([]);

    // Fetch departments based on sub-company and active status
    const getDepartments = async () => {
        if (!toggleCompany || toggleCompany === "null") return;
        try {
            const res = await axios.get(
                `${BASE_URL}/wfm/departmentfilterbysubcompanycount/${toggleCompany}/${empStatus}/`
            );
            setDepartmentLists(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Trigger department fetch when SubCompany or Employee Status changes
    useEffect(() => {
        getDepartments();
    }, [toggleCompany, empStatus]);

    // ================= API CALL (switch endpoint by tab) =================
    const fetchRankings = async () => {
        if (!toggleCompany || toggleCompany === "null") return;
        setLoading(true);

        try {
            const params = { sub_company: toggleCompany };
            if (startMonthYear) params.start_month_year = startMonthYear;
            if (endMonthYear) params.end_month_year = endMonthYear;
            if (deptFilter && deptFilter !== "null") params.department_id = deptFilter;
            if (empStatus) params.employee_status = empStatus;
            if (rankFrom) params.rank_from = rankFrom;
            if (rankTo) params.rank_to = rankTo;
            if (sortOrder) params.sort_order = sortOrder;

            const endpoint =
                activeTab === "attendance"
                    ? "star-performance-attendance-rating-by-admin-wise/"
                    : activeTab === "letter"
                        ? "star-performance-letter-rating-by-admin-wise/"
                        : "star-performance-overall-rating/";

            const res = await axios.get(`${BASE_URL}/wfm/${endpoint}`, { params });
            setRows(res.data?.employees || []);

            if (activeTab === "letter") {
                setLetterConfig(res.data?.config || null);
            }

            // ADD
            if (activeTab === "overall") {
                setOverallTotals({
                    attendance_total_marks: res.data?.attendance_total_marks ?? "-",
                    letter_total_marks: res.data?.letter_total_marks ?? "-",
                    overall_total_marks: res.data?.overall_total_marks ?? "-",
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Trigger automatically when API-dependent filters OR the active tab changes
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchRankings();
        }, 400);
        return () => clearTimeout(delayDebounceFn);
    }, [
        toggleCompany,
        startMonthYear,
        endMonthYear,
        deptFilter,
        empStatus,
        rankFrom,
        rankTo,
        sortOrder,
        activeTab,
    ]);

    // Close modal when switching tabs
    useEffect(() => {
        setIsModalOpen(false);
        setModalData(null);
    }, [activeTab]);

    // ================= LOCAL FILTERING LOGIC =================
    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const empQ = empSearch.toLowerCase();
            const rhQ = rhSearch.toLowerCase();

            const matchEmp =
                !empQ ||
                (row.name && row.name.toLowerCase().includes(empQ)) ||
                (row.empcode && row.empcode.toLowerCase().includes(empQ));

            const matchRh =
                !rhQ ||
                (row.reporting_head_name && row.reporting_head_name.toLowerCase().includes(rhQ)) ||
                (row.reporting_head_id && row.reporting_head_id.toLowerCase().includes(rhQ));

            return matchEmp && matchRh;
        });
    }, [rows, empSearch, rhSearch]);

    const deptOptions = useMemo(() => {
        return departmentLists
            .sort((a, b) => a.title?.localeCompare(b.title))
            .map((dep) => ({
                value: dep.id,
                label: dep.title,
            }));
    }, [departmentLists]);

    const handleViewDetails = (row) => {
        setModalData(row);
        setIsModalOpen(true);
    };

    const handleDownloadAll = () => {
        setIsExporting(true);
        setTimeout(() => {
            if (hiddenDownloadBtnRef.current) {
                hiddenDownloadBtnRef.current.click();
            }
            setIsExporting(false);
        }, 500);
    };

    // Renders 5 stars. `maxMarks` is the total marks the final score is out of.
    const renderStars = (finalMarks, maxMarks, idPrefix, size = 20) => {
        // 1. Calculate exact proportional score out of 5
        const exactScoreOutOf5 = Math.min(Number(finalMarks || 0) / (maxMarks / 5), 5);

        // 2. Round UP to nearest 0.5 step
        const scoreOutOf5 = Math.ceil(exactScoreOutOf5 * 2) / 2;

        return (
            <div style={{ display: "flex", gap: "2px", alignItems: "center", justifyContent: "center" }}>
                {[1, 2, 3, 4, 5].map((index) => {
                    let fill = 0;

                    // Full star
                    if (scoreOutOf5 >= index) {
                        fill = 100;
                    }
                    // Half star
                    else if (scoreOutOf5 > index - 1) {
                        fill = 50;
                    }

                    return (
                        <StarIcon
                            key={index}
                            id={`${idPrefix}-${index}`}
                            fillPercentage={fill}
                            size={size}
                        />
                    );
                })}
            </div>
        );
    };

    const CSSForFilterDropdown = {
        control: (provided) => ({
            ...provided,
            minHeight: "38px",
            border: "none",
            boxShadow: "none",
        }),
        indicatorSeparator: () => ({ display: "none" }),
    };

    const tabButtonStyle = (isActive) => ({
        padding: "10px 22px",
        background: "none",
        border: "none",
        borderBottom: isActive ? "2px solid #5cb85c" : "2px solid transparent",
        color: isActive ? "#5cb85c" : "#666",
        fontWeight: isActive ? 600 : 500,
        fontSize: "14px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        outline: "none",
    });

    return (
        <>
            <div className="attendance-history-cont">
                {/* ================= TABS ================= */}
                <div className="bloc-tabss  wfm-margin" style={{ marginLeft: '0px' }}>
                    <button
                        className={activeTab === "attendance" ? "dashboardcomponent-tabs  font-weight400  font-size-subheading   dashboardcomponent-tabss dashboardcomponent-active-tabs" : "dashboardcomponent-tabs  font-weight400  font-size-subheading   dashboardcomponent-tabss"}
                        onClick={() => setActiveTab("attendance")}
                    >
                        Attendance Ranking
                    </button>
                    <button
                        className={activeTab === "letter" ? "dashboardcomponent-tabs  font-weight400  font-size-subheading   dashboardcomponent-tabss dashboardcomponent-active-tabs" : "dashboardcomponent-tabs  font-weight400  font-size-subheading   dashboardcomponent-tabss"}
                        onClick={() => setActiveTab("letter")}
                    >
                        Letter Ranking
                    </button>
                    <button
                        className={
                            activeTab === "overall"
                                ? "dashboardcomponent-tabs font-weight400 font-size-subheading dashboardcomponent-tabss dashboardcomponent-active-tabs"
                                : "dashboardcomponent-tabs font-weight400 font-size-subheading dashboardcomponent-tabss"
                        }
                        onClick={() => setActiveTab("overall")}
                    >
                        Overall Ranking
                    </button>
                </div>

                <div className="attendance-subcont">
                    <div className="field-cont">
                        {/* SubCompany Selection (SuperAdmin) */}
                        {SUPER_ADMIN && (
                            <div className="field-cont-div">
                                <Office />
                                <select
                                    className="attendance-input-field width-10vw date-field"
                                    value={toggleCompany}
                                    onChange={(e) => setToggleCompany(e.target.value)}
                                >
                                    <option value="null">SubCompany</option>
                                    {SUB_COMPANIES.sort((a, b) => a.title?.localeCompare(b.title)).map(
                                        (i, index) => (
                                            <option key={index} value={i.id}>
                                                {i.title}
                                            </option>
                                        )
                                    )}
                                </select>
                                <hr className="field-cont-hr" />
                            </div>
                        )}

                        {/* Start Month */}
                        <div className="field-cont-div">
                            <Calender />
                            <div className="date-uppercase">
                                <input
                                    type="month"
                                    className="attendance-input-field calender-input width-10vw"
                                    value={startMonthYear}
                                    onChange={(e) => setStartMonthYear(e.target.value)}
                                    max={getNextMonthYear()}
                                    title="Start Month"
                                />
                            </div>
                            <hr className="field-cont-hr" />
                        </div>

                        {/* End Month */}
                        <div className="field-cont-div">
                            <Calender />
                            <div className="date-uppercase">
                                <input
                                    type="month"
                                    className="attendance-input-field calender-input width-10vw"
                                    value={endMonthYear}
                                    onChange={(e) => setEndMonthYear(e.target.value)}
                                    max={getNextMonthYear()}
                                    title="End Month"
                                />
                            </div>
                            <hr className="field-cont-hr" />
                        </div>

                        {/* API: Dept Filter */}
                        <div className="field-cont-div">
                            <CircularGrid />
                            <select
                                className="attendance-input-field width-15vw date-field"
                                value={deptFilter}
                                onChange={(e) => setDeptFilter(e.target.value || "null")}
                            >
                                <option value="null">All Department</option>
                                {deptOptions
                                    .slice()
                                    .sort((a, b) => a.label.localeCompare(b.label))
                                    .map((i) => (
                                        <option key={i.value} value={i.value}>
                                            {i.label}
                                        </option>
                                    ))}
                            </select>
                            <hr className="field-cont-hr" />
                        </div>

                        {/* Local Search: Employee */}
                        <div className="field-cont-div">
                            <SearchBig />
                            <input
                                className="attendance-input-field width-15vw"
                                placeholder="Name/Code"
                                type="text"
                                value={empSearch}
                                onChange={(e) => setEmpSearch(e.target.value)}
                            />
                            <hr className="field-cont-hr" />
                        </div>

                        {/* Local Search: RH */}
                        <div className="field-cont-div">
                            <SearchBig />
                            <input
                                className="attendance-input-field width-10vw"
                                placeholder="Reporting Head"
                                type="text"
                                value={rhSearch}
                                onChange={(e) => setRhSearch(e.target.value)}
                            />
                            <hr className="field-cont-hr" />
                        </div>

                        {/* API: Status */}
                        <div className="field-cont-div">
                            <Active />
                            <select
                                className="attendance-input-field width-10vw date-field"
                                value={empStatus}
                                onChange={(e) => setEmpStatus(e.target.value)}
                            >
                                <option value="null">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                            <hr className="field-cont-hr" />
                        </div>

                        {/* API: Rank Range */}
                        <div className="field-cont-div">
                            <input
                                type="number"
                                min="1"
                                className="attendance-input-field width-10vw"
                                style={{ textAlign: "center", paddingLeft: "5px" }}
                                placeholder="Min Rank"
                                value={rankFrom}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || Number(value) >= 1) {
                                        setRankFrom(value);
                                    }
                                }}
                            />
                            <span style={{ margin: "0 5px", color: "#666" }}>-</span>
                            <input
                                type="number"
                                min="1"
                                className="attendance-input-field width-10vw"
                                style={{ textAlign: "center", paddingLeft: "5px" }}
                                placeholder="Max Rank"
                                value={rankTo}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || Number(value) >= 1) {
                                        setRankTo(value);
                                    }
                                }}
                            />
                            <hr className="field-cont-hr" />
                        </div>

                        {/* API: Sort Order */}
                        <div className="field-cont-div">
                            <select
                                className="attendance-input-field width-10vw date-field"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="high_to_low">High To Low</option>
                                <option value="low_to_high">Low To High</option>
                            </select>
                            <hr className="field-cont-hr" />
                        </div>
                    </div>

                    <div className="btn-cont">
                        <button
                            className="model-button-print"
                            onClick={handleDownloadAll}
                            disabled={isExporting}
                        >
                            <DownloadIcon />
                            {isExporting && (
                                <span style={{ marginLeft: "5px", fontSize: "12px" }}>Wait...</span>
                            )}
                        </button>

                        <div style={{ display: "none" }}>
                            <DownloadTableExcel
                                filename={
                                    activeTab === "attendance"
                                        ? "AttendanceRanking"
                                        : activeTab === "letter"
                                            ? "LetterRanking"
                                            : "OverallRanking"
                                }
                                sheet="users"
                                currentTableRef={tableRef.current}
                            >
                                <button ref={hiddenDownloadBtnRef}>Hidden Download</button>
                            </DownloadTableExcel>
                        </div>
                    </div>
                </div>



                {/* ================= VISIBLE UI TABLE ================= */}
                <div className="table-css-white-background" style={{ marginTop: "0" }}>
                    <div className="table-heading-flex">
                        <div className="repo-heading font-weight500 font-size-heading">
                            {activeTab === "attendance"
                                ? "Employee Attendance Rankings"
                                : activeTab === "letter"
                                    ? "Employee Letter Rankings"
                                    : "Employee Overall Rankings"}
                        </div>
                    </div>

                    {/* ---------- ATTENDANCE TABLE ---------- */}
                    {activeTab === "attendance" && (
                        <table ref={tableRef} className="table-css">
                            <thead>
                                <tr className="custom-table-head-tr">
                                    <th className="align-center">S.no</th>
                                    <th className="align-center">Employee</th>
                                    <th className="align-center">Rank</th>
                                    <th className="align-center">Designation</th>
                                    <th className="align-center">Department</th>
                                    <th className="align-center">Reporting Head</th>
                                    <th className="align-center">Total Marks</th>
                                    <th className="align-center">Final Marks</th>
                                    <th className="align-center">Rating (Out of 5)</th>
                                    <th className="align-center">Details</th>
                                </tr>
                            </thead>
                            {loading ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="10">
                                            <div className="spinner"></div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : filteredRows.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="10" className="align-center">
                                            No records found
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {filteredRows.map((row, index) => {
                                        const att = row.attendance_rating;

                                        return (
                                            <React.Fragment key={index}>
                                                <tr className="tr-border-bottom">
                                                    <td colSpan="10"></td>
                                                </tr>
                                                <tr className="custom-table-head-td">
                                                    <td className="align-center ">
                                                        {index + 1}.
                                                    </td>


                                                    <td className="align-center">
                                                        <CustomImageModal customStyle>
                                                            {row.profilepic ? (
                                                                <img
                                                                    src={`${IMAGE_URL}${row.profilepic}`}
                                                                    className="profile-img"
                                                                    alt={row?.name}
                                                                />
                                                            ) : (
                                                                <UserDefaultLogo
                                                                    user={{
                                                                        name: row.name,
                                                                        profilepic: "",
                                                                    }}
                                                                    customStyleforParent={
                                                                        "reqOutDuty-default-logo"
                                                                    }
                                                                    customStyleforChild={
                                                                        "small-profile"
                                                                    }
                                                                />
                                                            )}
                                                        </CustomImageModal>
                                                        <p>
                                                            {row.empcode}-{row.name}
                                                        </p>
                                                    </td>
                                                    <td className="align-center font-weight-bold">
                                                        {getOrdinal(row.rank)}
                                                    </td>

                                                    <td className="align-center">
                                                        {row.designation_name || "-"}
                                                    </td>
                                                    <td className="align-center">
                                                        {row.department_name || "-"}
                                                    </td>
                                                    <td className="align-center">
                                                        {row.reporting_head_name || "-"}
                                                    </td>
                                                    <td className="align-center">
                                                        {att?.total_marks || "0.00"}
                                                    </td>

                                                    <td
                                                        className="align-center"
                                                        style={{ color: "#5cb85c" }}
                                                    >
                                                        {att?.final_marks || "0.00"}
                                                    </td>

                                                    <td className="align-center">
                                                        {renderStars(
                                                            att?.final_marks || 0,
                                                            att?.total_marks || 10,
                                                            row.empcode
                                                        )}
                                                    </td>

                                                    <td className="align-center">
                                                        <button
                                                            onClick={() =>
                                                                handleViewDetails(row)
                                                            }
                                                            className="model-edit-button"
                                                            title="View Deduction Details"
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                            }}
                                                        >
                                                            <Eye />
                                                        </button>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            )}
                        </table>
                    )}

                    {/* ---------- LETTER TABLE ---------- */}
                    {activeTab === "letter" && (
                        <table ref={tableRef} className="table-css">
                            <thead>
                                <tr className="custom-table-head-tr">
                                    <th className="align-center">S.no</th>
                                    <th className="align-center">Employee</th>
                                    <th className="align-center">Rank</th>
                                    <th className="align-center">Designation</th>
                                    <th className="align-center">Department</th>
                                    <th className="align-center">Reporting Head</th>
                                    <th className="align-center">Total Marks</th>
                                    <th className="align-center">Added</th>
                                    <th className="align-center">Deducted</th>
                                    <th className="align-center">Final Marks</th>
                                    <th className="align-center">Rating (Out of 5)</th>
                                    <th className="align-center">Details</th>
                                </tr>
                            </thead>
                            {loading ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="12">
                                            <div className="spinner"></div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : filteredRows.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="12" className="align-center">
                                            No records found
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {filteredRows.map((row, index) => {
                                        const lr = row.letter_rating;

                                        return (
                                            <React.Fragment key={index}>
                                                <tr className="tr-border-bottom">
                                                    <td colSpan="12"></td>
                                                </tr>
                                                <tr className="custom-table-head-td">
                                                    <td className="align-center ">
                                                        {index + 1}.
                                                    </td>



                                                    <td className="align-center">
                                                        <CustomImageModal customStyle>
                                                            {row.profilepic ? (
                                                                <img
                                                                    src={`${IMAGE_URL}${row.profilepic}`}
                                                                    className="profile-img"
                                                                    alt={row?.name}
                                                                />
                                                            ) : (
                                                                <UserDefaultLogo
                                                                    user={{
                                                                        name: row.name,
                                                                        profilepic: "",
                                                                    }}
                                                                    customStyleforParent={
                                                                        "reqOutDuty-default-logo"
                                                                    }
                                                                    customStyleforChild={
                                                                        "small-profile"
                                                                    }
                                                                />
                                                            )}
                                                        </CustomImageModal>
                                                        <p>
                                                            {row.empcode}-{row.name}
                                                        </p>
                                                    </td>

                                                    <td className="align-center font-weight-bold">
                                                        {getOrdinal(row.rank)}
                                                    </td>

                                                    <td className="align-center">
                                                        {row.designation_name || "-"}
                                                    </td>
                                                    <td className="align-center">
                                                        {row.department_name || "-"}
                                                    </td>
                                                    <td className="align-center">
                                                        {row.reporting_head_name || "-"}
                                                    </td>

                                                    <td className="align-center">
                                                        {letterConfig?.total_marks ?? "-"}
                                                    </td>
                                                    <td className="align-center" style={{ color: "#5cb85c" }}>
                                                        +{lr?.marks_added || "0.00"}
                                                    </td>
                                                    <td className="align-center" style={{ color: "#d9534f" }}>
                                                        -{lr?.marks_deducted || "0.00"}
                                                    </td>
                                                    <td className="align-center" style={{ color: "#5cb85c", fontWeight: 600 }}>
                                                        {lr?.final_marks || "0.00"}
                                                    </td>

                                                    <td className="align-center">
                                                        {renderStars(
                                                            lr?.final_marks || 0,
                                                            letterConfig?.total_marks || 20,
                                                            row.empcode
                                                        )}
                                                    </td>

                                                    <td className="align-center">
                                                        <button
                                                            onClick={() =>
                                                                handleViewDetails(row)
                                                            }
                                                            className="model-edit-button"
                                                            title="View Letter Details"
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                position: "relative",
                                                            }}
                                                        >
                                                            <Eye />
                                                            {lr?.letter_count > 0 && (
                                                                <span
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "-6px",
                                                                        right: "-6px",
                                                                        background: "#5cb85c",
                                                                        color: "#fff",
                                                                        borderRadius: "50%",
                                                                        fontSize: "10px",
                                                                        width: "16px",
                                                                        height: "16px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {lr.letter_count}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            )}
                        </table>
                    )}

                    {/* ---------- OVERALL TABLE ---------- */}
                    {activeTab === "overall" && (
                        <table ref={tableRef} className="table-css">
                            <thead>
                                <tr className="custom-table-head-tr">
                                    <th className="align-center">S.no</th>
                                    <th className="align-center">Employee</th>
                                    <th className="align-center">Rank</th>
                                    <th className="align-center">Designation</th>
                                    <th className="align-center">Department</th>
                                    <th className="align-center">Reporting Head</th>
                                    <th className="align-center">Overall Total Marks</th>
                                    <th className="align-center">Attendance Marks</th>
                                    <th className="align-center">Letter Marks</th>
                                    <th className="align-center">Overall Marks</th>
                                    <th className="align-center">Rating (Out of 5)</th>
                                </tr>
                            </thead>
                            {loading ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="11">
                                            <div className="spinner"></div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : filteredRows.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="11" className="align-center">
                                            No records found
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {filteredRows.map((row, index) => (
                                        <React.Fragment key={index}>
                                            <tr className="tr-border-bottom">
                                                <td colSpan="11"></td>
                                            </tr>
                                            <tr className="custom-table-head-td">
                                                <td className="align-center ">
                                                    {index + 1}.
                                                </td>



                                                <td className="align-center">
                                                    <CustomImageModal customStyle>
                                                        {row.profilepic ? (
                                                            <img
                                                                src={`${IMAGE_URL}${row.profilepic}`}
                                                                className="profile-img"
                                                                alt={row?.name}
                                                            />
                                                        ) : (
                                                            <UserDefaultLogo
                                                                user={{ name: row.name, profilepic: "" }}
                                                                customStyleforParent={"reqOutDuty-default-logo"}
                                                                customStyleforChild={"small-profile"}
                                                            />
                                                        )}
                                                    </CustomImageModal>
                                                    <p>
                                                        {row.empcode}-{row.name}
                                                    </p>
                                                </td>

                                                <td className="align-center font-weight-bold">
                                                    {getOrdinal(row.sub_company_rank ?? "-")}
                                                </td>

                                                <td className="align-center">{row.designation_name || "-"}</td>
                                                <td className="align-center">{row.department_name || "-"}</td>
                                                <td className="align-center">{row.reporting_head_name || "-"}</td>

                                                <td className="align-center">
                                                    {overallTotals.overall_total_marks ?? "-"}
                                                </td>

                                                <td className="align-center">
                                                    {row.attendance_final_marks || "0.00"}
                                                </td>
                                                <td className="align-center">
                                                    {row.letter_final_marks || "0.00"}
                                                </td>
                                                <td
                                                    className="align-center"
                                                    style={{ color: "#5cb85c", fontWeight: 600 }}
                                                >
                                                    {row.overall_final_marks || "0.00"}
                                                </td>

                                                <td className="align-center">
                                                    {renderStars(
                                                        row.overall_final_marks || 0,
                                                        Number(overallTotals.overall_total_marks) || 30,
                                                        row.empcode
                                                    )}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            )}
                        </table>
                    )}
                </div>

                {/* ================= ATTENDANCE DETAILS MODAL ================= */}
                {isModalOpen && modalData && activeTab === "attendance" && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            zIndex: 9999,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                padding: "25px",
                                borderRadius: "8px",
                                width: "450px",
                                maxWidth: "90%",
                                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: "2px solid #f0f0f0",
                                    paddingBottom: "15px",
                                    marginBottom: "20px",
                                }}
                            >
                                <h3 style={{ margin: 0, fontSize: "1.2rem", color: "#333" }}>
                                    Deduction Details
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        fontSize: "1.5rem",
                                        cursor: "pointer",
                                        color: "#888",
                                    }}
                                >
                                    &times;
                                </button>
                            </div>

                            <div
                                style={{
                                    marginBottom: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <UserDefaultLogo
                                    user={{ name: modalData.name, profilepic: null }}
                                    customStyleforParent={""}
                                    customStyleforChild={"small-profile"}
                                />
                                <div>
                                    <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                        {modalData.name}
                                    </div>
                                    <div style={{ color: "#666", fontSize: "0.9rem" }}>
                                        {modalData.empcode} | {modalData.department_name}
                                    </div>
                                </div>
                            </div>

                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    textAlign: "left",
                                    fontSize: "0.95rem",
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            borderBottom: "1px solid #ddd",
                                            backgroundColor: "#f9f9f9",
                                        }}
                                    >
                                        <th style={{ padding: "10px" }}>Type</th>
                                        <th style={{ padding: "10px", textAlign: "center" }}>
                                            Count
                                        </th>
                                        <th style={{ padding: "10px", textAlign: "right" }}>
                                            Deduction
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                                        <td style={{ padding: "10px" }}>Half Days</td>
                                        <td style={{ padding: "10px", textAlign: "center" }}>
                                            {modalData.attendance_rating?.halfday?.count || 0}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px",
                                                textAlign: "right",
                                                color: "#d9534f",
                                            }}
                                        >
                                            -
                                            {modalData.attendance_rating?.halfday?.deduction ||
                                                "0.00"}
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                                        <td style={{ padding: "10px" }}>Absents</td>
                                        <td style={{ padding: "10px", textAlign: "center" }}>
                                            {modalData.attendance_rating?.absent?.count || 0}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px",
                                                textAlign: "right",
                                                color: "#d9534f",
                                            }}
                                        >
                                            -
                                            {modalData.attendance_rating?.absent?.deduction ||
                                                "0.00"}
                                        </td>
                                    </tr>
                                    <tr style={{ borderBottom: "2px solid #ddd" }}>
                                        <td style={{ padding: "10px" }}>Lates</td>
                                        <td style={{ padding: "10px", textAlign: "center" }}>
                                            {modalData.attendance_rating?.late?.count || 0}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px",
                                                textAlign: "right",
                                                color: "#d9534f",
                                            }}
                                        >
                                            -
                                            {modalData.attendance_rating?.late?.deduction ||
                                                "0.00"}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginTop: "15px",
                                    padding: "10px",
                                    backgroundColor: "#fdfdfd",
                                    borderRadius: "5px",
                                }}
                            >
                                <span style={{ fontWeight: "bold" }}>Total Deduction:</span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#d9534f",
                                        fontSize: "1.1rem",
                                    }}
                                >
                                    -
                                    {modalData.attendance_rating?.total_deduction || "0.00"}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= LETTER DETAILS MODAL ================= */}
                {isModalOpen && modalData && activeTab === "letter" && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            zIndex: 9999,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                padding: "25px",
                                borderRadius: "8px",
                                width: "640px",
                                maxWidth: "92%",
                                maxHeight: "85vh",
                                overflowY: "auto",
                                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: "2px solid #f0f0f0",
                                    paddingBottom: "15px",
                                    marginBottom: "20px",
                                }}
                            >
                                <h3 style={{ margin: 0, fontSize: "1.2rem", color: "#333" }}>
                                    Letter Details
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        fontSize: "1.5rem",
                                        cursor: "pointer",
                                        color: "#888",
                                    }}
                                >
                                    &times;
                                </button>
                            </div>

                            <div
                                style={{
                                    marginBottom: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <UserDefaultLogo
                                    user={{ name: modalData.name, profilepic: null }}
                                    customStyleforParent={""}
                                    customStyleforChild={"small-profile"}
                                />
                                <div>
                                    <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                        {modalData.name}
                                    </div>
                                    <div style={{ color: "#666", fontSize: "0.9rem" }}>
                                        {modalData.empcode} | {modalData.department_name}
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, 1fr)",
                                    gap: "10px",
                                    marginBottom: "18px",
                                }}
                            >
                                <div style={summaryBoxStyle("#f7f7f7", "#333")}>
                                    <div style={summaryLabelStyle}>Total</div>
                                    <div style={summaryValueStyle}>{letterConfig?.total_marks ?? "-"}</div>
                                </div>
                                <div style={summaryBoxStyle("#eef9ee", "#5cb85c")}>
                                    <div style={summaryLabelStyle}>Added</div>
                                    <div style={summaryValueStyle}>
                                        +{modalData.letter_rating?.marks_added || "0.00"}
                                    </div>
                                </div>
                                <div style={summaryBoxStyle("#fdeeee", "#d9534f")}>
                                    <div style={summaryLabelStyle}>Deducted</div>
                                    <div style={summaryValueStyle}>
                                        -{modalData.letter_rating?.marks_deducted || "0.00"}
                                    </div>
                                </div>
                                <div style={summaryBoxStyle("#eef4ff", "#2a6fdb")}>
                                    <div style={summaryLabelStyle}>Final</div>
                                    <div style={summaryValueStyle}>
                                        {modalData.letter_rating?.final_marks || "0.00"}
                                    </div>
                                </div>
                            </div>

                            {/* Letters list */}
                            {modalData.letters && modalData.letters.length > 0 ? (
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        textAlign: "left",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    <thead>
                                        <tr
                                            style={{
                                                borderBottom: "1px solid #ddd",
                                                backgroundColor: "#f9f9f9",
                                            }}
                                        >
                                            <th style={{ padding: "10px" }}>Letter</th>
                                            <th style={{ padding: "10px" }}>Type</th>
                                            <th style={{ padding: "10px" }}>Issued By</th>
                                            <th style={{ padding: "10px" }}>Date</th>
                                            <th
                                                style={{
                                                    padding: "10px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                Marks
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalData.letters.map((l, i) => {
                                            const delta = Number(l.marks_delta || 0);
                                            return (
                                                <tr
                                                    key={i}
                                                    style={{
                                                        borderBottom: "1px solid #f0f0f0",
                                                    }}
                                                >
                                                    <td style={{ padding: "10px" }}>
                                                        {l.letter_name || "-"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "10px",
                                                            textTransform: "capitalize",
                                                        }}
                                                    >
                                                        {l.letter_type || "-"}
                                                    </td>
                                                    <td style={{ padding: "10px" }}>
                                                        {l.issued_by || "-"}
                                                    </td>
                                                    <td style={{ padding: "10px" }}>
                                                        {l.issued_at
                                                            ? new Date(
                                                                l.issued_at
                                                            ).toLocaleDateString()
                                                            : "-"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "10px",
                                                            textAlign: "right",
                                                            fontWeight: 600,
                                                            color:
                                                                delta < 0
                                                                    ? "#d9534f"
                                                                    : "#5cb85c",
                                                        }}
                                                    >
                                                        {delta > 0 ? "+" : ""}
                                                        {l.marks_delta}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "25px",
                                        color: "#888",
                                        fontSize: "0.95rem",
                                        border: "1px dashed #ddd",
                                        borderRadius: "6px",
                                    }}
                                >
                                    No letters issued in this period
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

const summaryBoxStyle = (bg, color) => ({
    background: bg,
    borderRadius: "6px",
    padding: "10px",
    textAlign: "center",
});

const summaryLabelStyle = {
    fontSize: "0.75rem",
    color: "#777",
    marginBottom: "4px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
};

const summaryValueStyle = {
    fontSize: "1rem",
    fontWeight: 700,
};

export default EmployeeStarRanking;